import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../../context/Language/LanguageContext";
import { getRotationLabels } from "../utils/Constants";

// Mutates `filter.items[].selected` in place and notifies the caller — kept
// as standalone exports so a parent (e.g. a collapse-title bar) can drive
// All/None without needing this component to render its own header.
export function selectAllItems(filter, handleOptionChanged) {
    filter.allselected = true;
    for (const item of filter.items) item.selected = true;
    handleOptionChanged(filter, null);
}

export function selectNoneItems(filter, handleOptionChanged) {
    filter.allselected = false;
    for (const item of filter.items) item.selected = false;
    handleOptionChanged(filter, null);
}

// Compact variant of MultiChoice.jsx: All/None are icon buttons sharing the
// title row (instead of a separate text-button row), smaller font/spacing,
// and an optional multi-column grid for lists of short phrases (attack
// combos, setter calls, ...) via the `columns` prop. Set `showHeader={false}`
// when the caller renders its own title/All/None elsewhere (e.g. hosted in a
// collapse-title bar next to the expand/collapse chevron).
function MultiChoiceCompact({ filter, handleOptionChanged, title, columns = 1, showHeader = true }) {
    const { t } = useLanguage();

    const displayName = (name) => {
        if (filter.title === "Rotations") {
            const labels = getRotationLabels();
            const idx = parseInt(name, 10) - 1;
            if (idx >= 0 && idx < labels.length) {
                return labels[idx];
            }
        }
        return t(name).toUpperCase();
    };

    const toggleOptionSelect = (item) => {
        if (filter.singleSelection) {
            item.selected = true;
            filter.selectedValues = [item.name];
            for (const xitem of filter.items) {
                if (xitem !== item) xitem.selected = false;
            }
        } else {
            item.selected = !item.selected;
            filter.selectedValues = [];
            let checked = 0;
            for (const xitem of filter.items) {
                checked += xitem.selected ? 1 : 0;
                if (xitem.selected) filter.selectedValues.push(xitem.name);
            }
            filter.allselected = checked === filter.items.length;
        }
        handleOptionChanged(filter, item);
    };

    if (filter.items === undefined) {
        return <></>;
    }

    return (
        <div>
            {showHeader && (
                <div className={`flex items-center gap-1 pb-px ${title ? "justify-between" : "justify-end"}`}>
                    {title ? <span className="text-[10px] font-semibold uppercase opacity-70 truncate">{title}</span> : null}
                    {filter.singleSelection === false && (
                        <div className="flex gap-0.5 shrink-0">
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs btn-square min-h-0 h-5 w-5 p-0"
                                title={t("Select All")}
                                onClick={() => selectAllItems(filter, handleOptionChanged)}
                            >
                                <CheckIcon className="w-3 h-3" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs btn-square min-h-0 h-5 w-5 p-0"
                                title={t("Select None")}
                                onClick={() => selectNoneItems(filter, handleOptionChanged)}
                            >
                                <XMarkIcon className="w-3 h-3" />
                            </button>
                        </div>
                    )}
                </div>
            )}
            <div
                className={columns > 1 ? "grid gap-x-2 gap-y-1.5" : "flex flex-col gap-y-1.5"}
                style={columns > 1 ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}
            >
                {filter.items.map((item, index) => (
                    <label
                        key={`${item.name}-${index}`}
                        htmlFor={`custom-checkbox-compact-${filter.title}-${item.name}`}
                        className="flex items-center gap-1 min-w-0 cursor-pointer"
                    >
                        <input
                            type="checkbox"
                            className="checkbox checkbox-xs rounded-none shrink-0"
                            id={`custom-checkbox-compact-${filter.title}-${item.name}`}
                            name={item.name}
                            value={item.name}
                            checked={item.selected}
                            onChange={() => toggleOptionSelect(item)}
                        />
                        <span className="text-[10px] text-base-content truncate">{displayName(item.name)}</span>
                        {item.amount > 0 && <span className="text-[10px] text-red-700 ml-auto shrink-0">{item.amount}</span>}
                    </label>
                ))}
            </div>
        </div>
    );
}

export default MultiChoiceCompact;
